import React, { useEffect, useRef, useState } from 'react';
import { mockClients } from '../mock';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ClientsSection = () => {
  const [visibleClients, setVisibleClients] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const clientRefs = useRef([]);
  const { language } = useLanguage();
  const t = translations[language].clients;

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await fetch(`${API}/clients`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setClients(data);
          } else {
            // Fallback to mock data if no clients in DB
            setClients(mockClients);
          }
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients(mockClients);
      }
      setLoading(false);
    };

    fetchClients();
  }, []);

  useEffect(() => {
    if (clients.length === 0) return;

    const observers = clientRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleClients((prev) => [...new Set([...prev, index])]);
            }
          });
        },
        { threshold: 0.1 }
      );

      if (ref) observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [clients]);

  if (loading) {
    return (
      <section id="clients" className="py-20 bg-slate-900 relative overflow-hidden">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-6 bg-slate-800 rounded w-32 mx-auto mb-4"></div>
            <div className="h-10 bg-slate-800 rounded w-48 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="clients" className="py-20 bg-slate-900 relative overflow-hidden" data-testid="clients-section">
      {/* Background Pink Glow */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-pink-500/40 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-pink-500/40 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h6 className="text-sm uppercase tracking-widest text-pink-400 mb-4 animate-fade-in">{t.subtitle}</h6>
          <h2 className="sm:text-5xl animate-fade-in !font-bold !text-4xl text-white">{t.title}</h2>
        </div>

        {/* Clients Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
          {clients.map((client, index) => (
            <div
              key={client.id}
              ref={(el) => (clientRefs.current[index] = el)}
              className={`group relative aspect-square bg-slate-800/80 backdrop-blur-sm rounded-lg overflow-hidden border border-slate-700 hover:border-pink-500/50 transition-all duration-500 transform hover:scale-105 ${
                visibleClients.includes(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{
                transitionDelay: `${index * 50}ms`
              }}
              data-testid={`client-logo-${index}`}
            >
              {/* Pink Glow on Hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-transparent to-blue-500/20"></div>
              </div>

              <div className="absolute inset-0 flex items-center justify-center p-4 relative z-10">
                <img
                  src={client.logo}
                  alt={client.name}
                  className="w-full h-full object-contain opacity-60 group-hover:opacity-100 transition-all duration-300 filter grayscale group-hover:grayscale-0"
                />
              </div>

              {/* Pink Corner Accent */}
              <div className="absolute top-0 right-0 w-8 h-8 bg-gradient-to-br from-pink-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
